import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Slide,
  Typography,
  Box,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled, keyframes } from "@mui/material/styles";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import VisibilityIcon from '@mui/icons-material/Visibility';
import BugReportIcon from "@mui/icons-material/BugReport";
import TelegramIcon from "@mui/icons-material/Telegram";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PropTypes from "prop-types";
import { withUserContext, withApiContext } from "context";

// Slide Transition from Bottom
const Transition = React.forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Fade-in and Scale Animation
const fadeInScale = keyframes`
    from {
        opacity: 0;
        transform: scale(0.9);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    animation: `${fadeInScale} 300ms cubic-bezier(0.4, 0, 0.2, 1)`,
    transformOrigin: "center center",
    boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
  },
}));

const AnimatedCloseButton = styled(IconButton)(({ theme }) => ({
  transition: "all 250ms ease-in-out",
  color: "#1e293b",
  maxWidth: "40px",
  maxHeight: "40px",
  "&:hover": {
    backgroundColor: "rgba(255,99,71,0.1)",
    transform: "rotate(90deg)",
    color: "#ee3349",
  },
  "&:active": {
    transform: "scale(0.9)",
  },
}));

const RevealAnimation = styled("div")(() => ({
  opacity: 0,
  transform: "translateY(20px)",
  animation: `${fadeInScale} 400ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
  animationDelay: "200ms",
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ReportIssuesModal = ({
  isOpen,
  onClose,
  onSubmit,
  ctx_user,
  ctx_api,
}) => {
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentName, setAttachmentName] = useState(null);
  const [courseList, setCourseList] = useState([]);
  const [assessmentList, setAssessmentList] = useState([]);
  const [courseSearch, setCourseSearch] = useState("");
  const [assessmentSearch, setAssessmentSearch] = useState("");

  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedAssessment, setSelectedAssessment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);

  const { user } = ctx_user;
  const member_id = user.id;
  const acad_year_id = user.acadYearId;
  const goi_id = user.goiId;
  const organizationId = user.organizationId;

  const getCourseAssessment = async () => {
    try {
      const data = await ctx_api.privateRequest({
        cmd: "get_course_test",
        args: { acadYearId: user.acadYearId, memberId: user.id },
      });

      const combination = data?.rsp?.[0]?.combination;

      if (
        Array.isArray(combination?.course) &&
        Array.isArray(combination?.test)
      ) {
        setCourseList(
          combination.course.map((course) => ({
            label: course.course_name,
            value: course.course_publish_id,
            id: course.course_id,
          }))
        );

        setAssessmentList(
          combination.test.map((test) => ({
            label: test.test_name,
            value: test.test_publish_id,
            id: test.test_id,
          }))
        );
      } else {
        console.warn("Unexpected response format:", data);
      }
    } catch (error) {
      console.error("Error fetching course tests:", error);
    }
  };

  const handleAttachmentPreview = () => {
    if (!attachment) return;
    setOpenPreview(true);
  };

  const handleAttachmentUpload = (event) => {
    const file = event.target.files[0];
    if (file  && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Image = reader.result;
            setAttachment(base64Image);
      };
      reader.readAsDataURL(file);
      setAttachmentName(file.name);
  }
  };

  const isInputValid = (value) => {
    return value.trim().length > 0;
  };

  const isDisabled =
    !isInputValid(description) ||
    (selectedCourse === "" && selectedAssessment === "");

  const handleSubmit = async () => {
    if (isDisabled) return;

    const formData = {
      ticketId: 0,
      ticketType: "content_concern",
      description: description.trim(),
      courseId: selectedCourse || null,
      testAssessmentId: selectedAssessment || null,
      attachment: attachment,
      memberId: member_id,
      acadYearId: acad_year_id,
      goiId: goi_id,
      isCompleted: 0,
      isAssigned: 0,
      organizationId: organizationId,
    };

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      setError(error.message || "An error occurred. Please try again.");
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCourseChange = (e) => {
    const value = e.target.value;
    setSelectedCourse(value);
    setSelectedAssessment("");
  };

  const handleAssessmentChange = (e) => {
    const value = e.target.value;
    setSelectedAssessment(value);
    setSelectedCourse("");
  };

  useEffect(() => {
    if (user?.id && user?.acadYearId) {
      getCourseAssessment();
    }
  }, [user]);

  return (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          color: "black",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <Box sx={{ display: "flex" }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", textAlign: "center" }}
            >
              CONTENT CONCERNS
            </Typography>
            <BugReportIcon
              sx={{
                fontSize: 35,
                color: "#2196f3",
              }}
            />
          </Box>
          <Box
            sx={{
              alignItems: "center",
              textAlign: "center",
              width: "100%",
              justifyContent: "center",
            }}
          >
            <Typography
              style={{
                textAlign: "center",
                fontSize: "12px",
                fontWeight: "100",
                opacity: "0.6",
              }}
            >
              Send us your content concerns and other issues
            </Typography>
          </Box>
        </Box>

        <AnimatedCloseButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </AnimatedCloseButton>
      </DialogTitle>

      <RevealAnimation>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            padding: "15px 30px",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box>
            <Typography>Course Issues?</Typography>
            <FormControl size="small">
              <Select
                value={selectedCourse}
                onChange={handleCourseChange}
                renderValue={(value) => {
                  if (value === "") return "Choose a Course";
                  const course = courseList.find((c) => c.value === value);
                  return course ? course.label : value;
                }}
                displayEmpty
                disabled={!!selectedAssessment}
                endAdornment={
                  selectedCourse ? (
                    <IconButton onClick={() => setSelectedCourse("")}>
                      <CloseIcon />
                    </IconButton>
                  ) : null
                }
                IconComponent={selectedCourse ? "" : ExpandMoreIcon}
                sx={{
                  padding: "0px 2px",
                  fontSize: "0.875rem",
                  minHeight: "20px",
                  borderRadius: "10px",
                  width: "100%",
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250,
                      overflowY: "auto",
                    },
                  },
                }}
              >
                <MenuItem disableRipple disableTouchRipple>
                  <TextField
                    autoFocus
                    placeholder="Search courses..."
                    size="small"
                    variant="outlined"
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  />
                </MenuItem>

                {/* Filtered list */}
                {[{ value: "", label: "Choose a Course", disabled: true },
                  ...courseList.filter((c) =>
                    c.label.toLowerCase().includes(courseSearch.toLowerCase())
                  ),
                ].map((course) => (
                  <MenuItem
                    key={course.value}
                    value={course.value}
                    disabled={course.disabled || false}
                  >
                    {course.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography>Assessment Issues?</Typography>
            <FormControl size="small">
              <Select
                value={selectedAssessment}
                onChange={handleAssessmentChange}
                renderValue={(value) => {
                  if (value === "") return "Choose an Assessment";
                  const assessment = assessmentList.find(
                    (a) => a.value === value
                  );
                  return assessment ? assessment.label : value;
                }}
                displayEmpty
                disabled={!!selectedCourse}
                endAdornment={
                  selectedAssessment ? (
                    <IconButton onClick={() => setSelectedAssessment("")}>
                      <CloseIcon />
                    </IconButton>
                  ) : null
                }
                IconComponent={selectedAssessment ? "" : ExpandMoreIcon}
                sx={{
                  padding: "0px 2px",
                  fontSize: "0.875rem",
                  minHeight: "20px",
                  borderRadius: "10px",
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250,
                      overflowY: "auto",
                    },
                  },
                }}
              >
                <MenuItem disableRipple disableTouchRipple>
                  <TextField
                    autoFocus
                    placeholder="Search assessments..."
                    size="small"
                    variant="outlined"
                    value={assessmentSearch}
                    onChange={(e) => setAssessmentSearch(e.target.value)}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                    }}
                    onKeyDown={(e) => e.stopPropagation()} // Prevents Select from stealing typing
                    onClick={(e) => e.stopPropagation()}   // Keeps dropdown open while typing
                  />
                </MenuItem>

                {/* Filtered assessment list */}
                {[{ value: "", label: "Choose an Assessment", disabled: true },
                  ...assessmentList.filter((a) =>
                    a.label.toLowerCase().includes(assessmentSearch.toLowerCase())
                  ),
                ].map((assessment) => (
                  <MenuItem
                    key={assessment.value}
                    value={assessment.value}
                    disabled={assessment.disabled || false}
                  >
                    {assessment.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </RevealAnimation>

      <div style={{ padding: "0px 30px" }}>
        <Typography>Any Other Concerns?</Typography>
        <TextField
          fullWidth
          label="Tell us more about your problem"
          variant="outlined"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          margin="normal"
          required
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              color: "#1e293b",
            },
          }}
        />
        <RevealAnimation>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                marginTop: 2,
                border: "1px solid rgba(0,0,0,0.23)",
                borderRadius: "12px",
                overflow: "hidden",
                width: "100%",
              }}
            >
              <Button
                variant="text"
                disabled={!attachment}
                onClick={handleAttachmentPreview}
                startIcon={<VisibilityIcon />}
                sx={{
                  flex: 1,
                  borderRadius: 0,
                  borderRight: "1px solid rgba(0,0,0,0.23)",
                  color: "#1e293b",
                }}
              >
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                />
              </Button>

              <Button
                component="label"
                variant="text"
                startIcon={<AttachFileIcon />}
                sx={{
                  flex: 1,
                  borderRadius: 0,
                  color: "#1e293b",
                  height: "44px",
                }}
              >
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handleAttachmentUpload}
                />
              </Button>
            </Box>

            {attachment && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 1, width: "100%" }}
              >
                Attachment: {attachmentName}
              </Typography>
            )}
          </Box>
        </RevealAnimation>
      </div>

      <DialogActions />
      {error && (
        <Typography
          variant="body2"
          color="error"
          sx={{ mt: 1, textAlign: "center" }}
        >
          {error}
        </Typography>
      )}
      <Button
        fullWidth
        startIcon={
          isSubmitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <TelegramIcon />
          )
        }
        variant="contained"
        sx={{
          marginBottom: 2,
          marginTop: 1,
          borderRadius: "12px",
          backgroundColor: "#1e293b",
          width: "92%",
          alignSelf: "center",
          "&:hover": {
            backgroundColor: "#334155",
          },
        }}
        onClick={handleSubmit}
        disabled={isDisabled || isSubmitting}
      >
        {isSubmitting ? "Submitting..." : "Send Your Request"}
      </Button>
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Attachment Preview</DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
        >
          {attachment && (
            <img
              src={attachment}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: "80vh" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </StyledDialog>
  );
};

ReportIssuesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

ReportIssuesModal.defaultProps = {
  description: "",
  children: null,
};

export default withUserContext(withApiContext(ReportIssuesModal));
